import { useMsal } from '@azure/msal-react'
import { Clock, UserX } from 'lucide-react'
import { useBrandingStore } from '../stores/branding'
import { useAuthStore } from '../stores/auth'

// Two distinct reasons a signed-in user lands here, both handled by this one
// component rather than two separate pages, since they're the same "you
// can't get in yet, here's what to do" shape:
//
//   - 'no_invite' (current model, closed enrollment): the token's
//     preferred_username didn't match any admin-created row at all. There is
//     nothing pending -- the user must be invited by an admin first. This is
//     this component's primary job now (see its filename).
//   - 'pending' (legacy): a self-service access request from before
//     enrollment closed, still sitting in the DB as status='pending'. Kept
//     working for any such row rather than dropped -- an admin can still
//     approve/deny it from Users.tsx exactly as before.
export default function NoInviteFoundPage() {
  const { instance } = useMsal()
  const { name } = useBrandingStore()
  const { authStatus, noInviteEmail } = useAuthStore()
  const isLegacyPending = authStatus === 'pending'

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md space-y-6 text-center">
        <div className="flex justify-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
            {isLegacyPending ? (
              <Clock className="h-8 w-8 text-muted-foreground" />
            ) : (
              <UserX className="h-8 w-8 text-muted-foreground" />
            )}
          </div>
        </div>

        {isLegacyPending ? (
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-foreground">Access Requested</h1>
            <p className="text-sm text-muted-foreground">
              Your request to access <strong>{name}</strong> is pending approval.
              An admin will review your request and grant you access.
            </p>
            <p className="text-xs text-muted-foreground">
              Once approved, sign in again to get started.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-foreground">No Access Yet</h1>
            <p className="text-sm text-muted-foreground">
              {name} doesn't have an invite for your account yet.
            </p>
            {noInviteEmail && (
              <p className="text-sm text-foreground">
                Your sign-in presented <strong>{noInviteEmail}</strong> — give this
                exact address to your administrator so they can create your account.
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Once an admin creates your account, sign in again to get started.
            </p>
          </div>
        )}

        <button
          onClick={() => instance.logoutRedirect()}
          className="text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground"
        >
          Sign out
        </button>
      </div>
    </div>
  )
}
